import { useGetAuthMe } from "@birdwatch/client";
import { useAuth } from "@clerk/clerk-react";
import { useState } from "react";
import { ThemeSwitch } from "@/components/theme-switch";
import Avatar from "@/components/ui/avatar/avatar";
import { Button } from "@/components/ui/button/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BwBookOpenLine,
  BwCaretDownMdLine,
  BwChatSquareHappyLine,
  BwLogoutLine,
} from "@/components/ui/icons";
import { EXTERNAL_URLS } from "@/constants/external-url";
import { ReferAMemberDialog } from "@/features/my/_components/refer-a-member-dialog";
import {
  exitImpersonation,
  useImpersonationSession,
} from "@/features/shared/impersonation";
import { useSupportChat } from "@/features/shared/support-chat";
import { ANALYTIC_EVENTS, AUTH_EVENTS, useAnalytics } from "@/libs/analytics";
import { clearMyPlaceIdStorage } from "../../_providers/my-place-context-provider";

export type UserDropdownMenuProps = {
  /** Optional controlled state for the refer dialog (e.g. when used with ReferMemberButton in my-place-layout). */
  referDialogOpen?: boolean;
  onReferDialogOpenChange?: (open: boolean) => void;
};

export function UserDropdownMenu({
  referDialogOpen: referDialogOpenProp,
  onReferDialogOpenChange: onReferDialogOpenChangeProp,
}: UserDropdownMenuProps = {}) {
  const { signOut } = useAuth();
  const impersonation = useImpersonationSession();
  const { resetAnalytics, sendEvent } = useAnalytics();
  const { show } = useSupportChat();
  const { data: me } = useGetAuthMe().get();
  const [internalReferOpen, setInternalReferOpen] = useState(false);

  const isReferDialogOpen =
    onReferDialogOpenChangeProp !== undefined
      ? (referDialogOpenProp ?? false)
      : internalReferOpen;
  const setReferDialogOpen =
    onReferDialogOpenChangeProp ?? setInternalReferOpen;

  if (!me?.id) {
    return null;
  }

  const handleSignOut = () => {
    // Inside the admin impersonation frame the session belongs to the admin;
    // signing out here would end THEIR session. Close the overlay instead.
    if (impersonation) {
      exitImpersonation();
      return;
    }
    if (typeof window !== "undefined") {
      clearMyPlaceIdStorage();
    }
    sendEvent(AUTH_EVENTS.SIGN_OUT);
    resetAnalytics();
    signOut();
  };

  const handleGetHelp = () => {
    show();
    sendEvent(ANALYTIC_EVENTS.HELP_CHAT_OPENED);
  };

  const userName = me.name || me.firstName || "User";
  const userAvatar = me.avatarUrl || "";

  return (
    <>
      <div className="flex gap-4 items-center shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              kind="ghost"
              variant="neutral"
              size="lg"
              className="p-1! gap-1! hover:bg-transparent"
              data-test="user-dropdown-menu-trigger"
            >
              <Avatar src={userAvatar} name={userName} size="md" />
              <BwCaretDownMdLine size={20} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-64"
            align="end"
            data-test="user-dropdown-menu-content"
          >
            <DropdownMenuGroup>
              <DropdownMenuItem
                onSelect={(e) => e.preventDefault()}
                className="cursor-default focus:bg-bg-dark-min py-0"
              >
                <ThemeSwitch className="min-w-0" />
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem className="flex gap-2" onClick={handleGetHelp}>
                <BwChatSquareHappyLine className="text-text-dark-high shrink-0 size-5" />
                <span className="grow text-sm text-text-dark-max">
                  Get help
                </span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />

            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="flex items-center gap-2">
                <BwBookOpenLine className="text-text-dark-high shrink-0 size-5" />
                <span className="grow text-sm text-text-dark-max">Legal</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent sideOffset={8} alignOffset={-4}>
                <DropdownMenuItem asChild>
                  <a
                    href={EXTERNAL_URLS.MEMBERSHIP_AGREEMENT}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Membership Agreement
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href={EXTERNAL_URLS.TERMS_OF_SERVICE}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms of Service
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href={EXTERNAL_URLS.PRIVACY_POLICY}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href={EXTERNAL_URLS.LICENSES_NUMBERS}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Licenses Numbers
                  </a>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={handleSignOut} className="flex gap-2">
              <BwLogoutLine className="text-text-dark-high shrink-0 size-5" />
              <span className="grow text-sm text-text-dark-max">
                {impersonation ? "Exit impersonation" : "Sign out"}
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ReferAMemberDialog
        open={isReferDialogOpen}
        onOpenChange={setReferDialogOpen}
      />
    </>
  );
}
